import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import tesoreriaCajaService, {
    ImportacionMedioCobroRenglon,
    ImportacionMediosCobroFilaMensaje,
    ImportacionMediosCobroResult,
} from '../../services/tesoreriaCajaService';
import { Imputable } from '../../services/imputableService';
import { GrupoCajaChica } from '../../services/tesoreriaService';
import { downloadBlob } from '../../utils/downloadBlob';
import { formatCurrency } from '../../utils/formatters';
import { resolverNroSucursalCheque } from '../../utils/chequeTerceroEstado';
import { useAuth } from '../../contexts/AuthContext';
import { effectiveUsername } from '../../utils/sistemaAuthz';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import SearchableSelect from '../SearchableSelect';
import GrupoCajaSelect from './GrupoCajaSelect';
import ImputableSearch from '../ImputableSearch';
import ImputableInlineSearchField from './ImputableInlineSearchField';

interface ImportarMediosCobroDialogProps {
    open: boolean;
    onClose: () => void;
    /** cargar: deja los renglones en el ingreso abierto. registrar: graba el ingreso vario. */
    modo?: 'cargar' | 'registrar';
    onCargado?: (renglones: ImportacionMedioCobroRenglon[]) => void;
    onRegistrado?: () => void;
}

const mediosDesdeRenglones = (renglones: ImportacionMedioCobroRenglon[]) =>
    renglones.map((r) => {
        const base: Record<string, unknown> = {
            mcp: r.mcp || '',
            importe: Number(r.importe) || 0,
            detalle: r.observaciones || '',
        };
        if (r.tipoPago === 'Cheque') {
            const esElectronico = Boolean(r.esElectronico);
            base.cheque = {
                nroBanco: r.nroBanco ?? 0,
                nroSucursal: resolverNroSucursalCheque(esElectronico, r.nroSucursal) ?? 0,
                nroCheque: r.nroCheque || '',
                nroLibrador: r.cuitLibrador || '',
                librador: r.librador || '',
                fechaEmision: r.fechaEmision ? new Date(r.fechaEmision).toISOString() : new Date().toISOString(),
                fechaVencimiento: r.fechaVencimiento ? new Date(r.fechaVencimiento).toISOString() : new Date().toISOString(),
                ctaBancaria: r.ctaBancaria || '',
                concepto: (r.observaciones || r.concepto || '').trim().slice(0, 30),
                esElectronico,
            };
        }
        return base;
    });

const fechaLocalIso = (isoDate: string) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0).toISOString();
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const extraerMensajeError = (error: any, porDefecto: string): string => {
    const data = error?.response?.data;
    if (!data) return porDefecto;
    if (typeof data === 'string') return data;
    if (data.errors && typeof data.errors === 'object') {
        const primera = Object.keys(data.errors)[0];
        const msg = (data.errors as Record<string, string[]>)[primera]?.[0];
        if (msg) return `${data.message ? `${data.message} ` : ''}${msg}`;
    }
    return data.message || data.title || porDefecto;
};

const TablaMensajes: React.FC<{ titulo: string; items: ImportacionMediosCobroFilaMensaje[]; color: 'error' | 'warning' }> = ({
    titulo,
    items,
    color,
}) => (
    <Box>
        <Typography variant="subtitle2" color={`${color}.main`} sx={{ mb: 0.5 }}>
            {titulo} ({items.length})
        </Typography>
        <TableContainer sx={{ maxHeight: 220, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 70 }}>Fila</TableCell>
                        <TableCell sx={{ width: 140 }}>Columna</TableCell>
                        <TableCell>Mensaje</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((e, i) => (
                        <TableRow key={`${e.fila}-${e.columna}-${i}`}>
                            <TableCell>{e.fila > 0 ? e.fila : 'General'}</TableCell>
                            <TableCell>{e.columna || '-'}</TableCell>
                            <TableCell>{e.mensaje}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const ImportarMediosCobroDialog: React.FC<ImportarMediosCobroDialogProps> = ({
    open,
    onClose,
    modo = 'cargar',
    onCargado,
    onRegistrado,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const { sucursales, loading: loadingSucursales, preferidaNro } = useSucursalesUsuarioContext();
    const usuario = effectiveUsername(user?.username) || 'Sistema';
    const registrarDirecto = modo === 'registrar';
    const [archivo, setArchivo] = useState<File | null>(null);
    const [omitirDuplicados, setOmitirDuplicados] = useState(false);
    const [resultado, setResultado] = useState<ImportacionMediosCobroResult | null>(null);
    const [validando, setValidando] = useState(false);
    const [registrando, setRegistrando] = useState(false);
    const [descargando, setDescargando] = useState(false);
    const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
    const [nroSuc, setNroSuc] = useState(0);
    const [imputable, setImputable] = useState<Imputable | null>(null);
    const [openImputable, setOpenImputable] = useState(false);
    const [grupo, setGrupo] = useState<GrupoCajaChica | null>(null);
    const [grupoKey, setGrupoKey] = useState(0);

    useEffect(() => {
        if (!open) return;
        setArchivo(null);
        setResultado(null);
        setOmitirDuplicados(false);
        setFecha(new Date().toISOString().slice(0, 10));
        setNroSuc(preferidaNro && preferidaNro > 0 ? preferidaNro : 0);
        setImputable(null);
        setGrupo(null);
        setGrupoKey((k) => k + 1);
    }, [open, preferidaNro]);

    const validacionOk = !!resultado && (resultado.errores?.length ?? 0) === 0 && (resultado.renglones?.length ?? 0) > 0;
    const ocupado = validando || registrando;

    const handleDescargarPlantilla = async () => {
        try {
            setDescargando(true);
            const blob = await tesoreriaCajaService.descargarPlantillaImportacionMediosCobro();
            downloadBlob(blob, 'Plantilla_Importacion_MediosCobro.xlsx', XLSX_MIME);
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'No se pudo descargar la plantilla'), { variant: 'error' });
        } finally {
            setDescargando(false);
        }
    };

    const handleArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (file && !/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
            enqueueSnackbar('Solo se admiten archivos .xlsx, .xlsm o .xls', { variant: 'warning' });
            return;
        }
        setArchivo(file);
        setResultado(null);
    };

    const validar = async () => {
        if (!archivo) {
            enqueueSnackbar('Seleccioná un archivo Excel', { variant: 'warning' });
            return null;
        }
        try {
            setValidando(true);
            const res = await tesoreriaCajaService.importarMediosCobro({
                archivo,
                omitirDuplicados,
            });
            setResultado(res);
            if ((res.errores?.length ?? 0) > 0) {
                enqueueSnackbar(`El archivo tiene ${res.errores.length} error(es). Corregilos y volvé a validar.`, { variant: 'warning' });
            }
            return res;
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'Error al validar el archivo'), {
                variant: 'error',
                autoHideDuration: 8000,
            });
            return null;
        } finally {
            setValidando(false);
        }
    };

    const cargar = async () => {
        const res = validacionOk && resultado ? resultado : await validar();
        if (!res || (res.errores?.length ?? 0) > 0 || (res.renglones?.length ?? 0) === 0) return;
        onCargado?.(res.renglones);
    };

    const registrar = async () => {
        if (!nroSuc || nroSuc <= 0) {
            enqueueSnackbar('Seleccioná la sucursal del ingreso', { variant: 'warning' });
            return;
        }
        if (!imputable) {
            enqueueSnackbar('Seleccioná el concepto del ingreso', { variant: 'warning' });
            return;
        }
        if (!grupo?.codigo) {
            enqueueSnackbar('Seleccioná el grupo de caja', { variant: 'warning' });
            return;
        }
        const res = validacionOk && resultado ? resultado : await validar();
        if (!res || (res.errores?.length ?? 0) > 0 || (res.renglones?.length ?? 0) === 0) return;
        try {
            setRegistrando(true);
            await api.post('/api/TesoreriaCaja/movimientos-caja', {
                fecha: fechaLocalIso(fecha),
                grupoMCP: grupo.codigo,
                nroSuc,
                tipoMovimiento: '5',
                codigoEntidad: imputable.nroCuenta,
                descripcion: imputable.nomCuenta || '',
                aclaracion: '',
                mediosDeCobro: mediosDesdeRenglones(res.renglones),
            });
            enqueueSnackbar('Ingreso registrado', { variant: 'success' });
            onRegistrado?.();
        } catch (error) {
            enqueueSnackbar(extraerMensajeError(error, 'No se pudo registrar el ingreso'), {
                variant: 'error',
                autoHideDuration: 8000,
            });
        } finally {
            setRegistrando(false);
        }
    };

    return (
        <Dialog open={open} onClose={ocupado ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>{registrarDirecto ? 'Importar medios y registrar ingreso' : 'Importar medios de cobro'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        {registrarDirecto
                            ? 'Se registra el ingreso vario con los medios del archivo. No pasa por la pantalla de carga.'
                            : 'Cada fila queda en el ingreso vario como si la hubieras cargado a mano. Después podés editar o borrar cada renglón antes de registrarlo.'}
                    </Typography>

                    {registrarDirecto && (
                        <Stack spacing={1.5}>
                            <SearchableSelect
                                label="Sucursal"
                                size="small"
                                required
                                loading={loadingSucursales}
                                options={sucursales.map((sucursal) => ({
                                    value: sucursal.nroSuc,
                                    label: sucursal.nombreSucursal?.trim()
                                        ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                                        : `Suc. ${sucursal.nroSuc}`,
                                }))}
                                value={nroSuc > 0 ? nroSuc : null}
                                onChange={(value) => setNroSuc(Number(value) || 0)}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                label="Fecha de emisión"
                                type="date"
                                value={fecha}
                                onChange={(e) => setFecha(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                disabled={ocupado}
                            />
                            {!imputable ? (
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <ImputableInlineSearchField
                                            label="Concepto imputable"
                                            placeholder="Número de cuenta o nombre"
                                            tipoMovimientoIE="I"
                                            onSelect={setImputable}
                                            onOpenModal={() => setOpenImputable(true)}
                                        />
                                    </Box>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={() => setOpenImputable(true)}
                                        disabled={ocupado}
                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25 }}
                                    >
                                        Seleccionar
                                    </Button>
                                </Stack>
                            ) : (
                                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                            <Box minWidth={0}>
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {imputable.nomCuenta || 'Sin nombre'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Cuenta #{imputable.nroCuenta}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row">
                                                <Tooltip title="Cambiar concepto">
                                                    <IconButton size="small" onClick={() => setOpenImputable(true)} disabled={ocupado}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Quitar concepto">
                                                    <IconButton size="small" color="error" onClick={() => setImputable(null)} disabled={ocupado}>
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}
                            <GrupoCajaSelect
                                key={grupoKey}
                                usuario={usuario}
                                value={grupo}
                                onChange={setGrupo}
                                required
                            />
                        </Stack>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
                            onClick={handleDescargarPlantilla}
                            disabled={descargando}
                            sx={{ textTransform: 'none' }}
                        >
                            Descargar plantilla
                        </Button>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            disabled={ocupado}
                            sx={{ textTransform: 'none' }}
                        >
                            Seleccionar archivo
                            <input type="file" hidden accept=".xlsx,.xlsm,.xls" onChange={handleArchivo} />
                        </Button>
                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                            {archivo ? archivo.name : 'Ningún archivo seleccionado'}
                        </Typography>
                    </Stack>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={omitirDuplicados}
                                onChange={(e) => {
                                    setOmitirDuplicados(e.target.checked);
                                    setResultado(null);
                                }}
                                disabled={ocupado}
                            />
                        }
                        label="Omitir cheques ya cargados en lugar de rechazar el archivo"
                    />

                    {resultado && (
                        <Stack spacing={2}>
                            <Alert severity={(resultado.errores?.length ?? 0) > 0 ? 'error' : 'info'}>
                                {(resultado.errores?.length ?? 0) > 0
                                    ? 'El archivo tiene errores. No se pueden cargar los renglones hasta corregirlos.'
                                    : registrarDirecto
                                        ? 'Lectura correcta. Confirmá para registrar el ingreso.'
                                        : 'Lectura correcta. Revisá los renglones y cargalos en el ingreso vario.'}
                            </Alert>

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={`Filas leídas: ${resultado.filasLeidas}`} />
                                <Chip size="small" color="success" variant="outlined" label={`Válidas: ${resultado.filasValidas}`} />
                                {(resultado.filasConError ?? 0) > 0 && (
                                    <Chip size="small" color="error" variant="outlined" label={`Con error: ${resultado.filasConError}`} />
                                )}
                                {(resultado.filasOmitidas ?? 0) > 0 && (
                                    <Chip size="small" color="warning" variant="outlined" label={`Omitidas: ${resultado.filasOmitidas}`} />
                                )}
                                <Chip size="small" label={`Cheques: ${resultado.cantidadCheques}`} />
                                <Chip size="small" color="primary" label={`Total: ${formatCurrency(resultado.importeTotal)}`} />
                            </Stack>

                            {(resultado.renglones?.length ?? 0) > 0 && (
                                <TableContainer sx={{ maxHeight: 280, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tipo</TableCell>
                                                <TableCell>MCP</TableCell>
                                                <TableCell>Detalle</TableCell>
                                                <TableCell>Observaciones</TableCell>
                                                <TableCell align="right">Importe</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {resultado.renglones.map((r) => (
                                                <TableRow key={r.fila}>
                                                    <TableCell>{r.tipoPago}</TableCell>
                                                    <TableCell>{r.mcp}</TableCell>
                                                    <TableCell>
                                                        {r.tipoPago === 'Cheque'
                                                            ? [r.nombreBanco, r.nroCheque ? `Nº ${r.nroCheque}` : ''].filter(Boolean).join(' · ')
                                                            : r.descripcionMcp || ''}
                                                    </TableCell>
                                                    <TableCell>{r.observaciones || '-'}</TableCell>
                                                    <TableCell align="right">{formatCurrency(r.importe)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {(resultado.errores?.length ?? 0) > 0 && (
                                <TablaMensajes titulo="Errores" items={resultado.errores} color="error" />
                            )}
                            {(resultado.advertencias?.length ?? 0) > 0 && (
                                <TablaMensajes titulo="Advertencias" items={resultado.advertencias} color="warning" />
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={ocupado} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant="outlined"
                    startIcon={validando ? <CircularProgress size={16} /> : <FactCheckIcon />}
                    onClick={() => void validar()}
                    disabled={!archivo || ocupado}
                    sx={{ textTransform: 'none' }}
                >
                    Validar
                </Button>
                {registrarDirecto ? (
                    <Button
                        variant="contained"
                        startIcon={ocupado ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                        onClick={() => void registrar()}
                        disabled={!archivo || ocupado || (!!resultado && !validacionOk)}
                        sx={{ textTransform: 'none' }}
                    >
                        Registrar ingreso
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        startIcon={validando ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                        onClick={() => void cargar()}
                        disabled={!archivo || ocupado || (!!resultado && !validacionOk)}
                        sx={{ textTransform: 'none' }}
                    >
                        Cargar en el ingreso
                    </Button>
                )}
            </DialogActions>
            {registrarDirecto && (
                <ImputableSearch
                    open={openImputable}
                    onClose={() => setOpenImputable(false)}
                    onSelect={(item) => {
                        setImputable(item);
                        setOpenImputable(false);
                    }}
                    tipoMovimientoIE="I"
                />
            )}
        </Dialog>
    );
};

export default ImportarMediosCobroDialog;
